import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { CalendarDays, IndianRupee, FlaskConical } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { inputCls, PageHeader, StatusBadge } from "./shared";

const fmt = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;

export default function StaffDay() {
  const [staff, setStaff] = useState([]);
  const [staffId, setStaffId] = useState(localStorage.getItem("staff_day_id") || "");
  const [data, setData] = useState(null);
  const [options, setOptions] = useState({ products: [], staff: [] });
  const [use, setUse] = useState({ product_id: "", qty: "", note: "" });
  const [todayUse, setTodayUse] = useState([]);

  const today = new Date().toLocaleDateString("en-CA");
  const loadUsage = useCallback(() => {
    api.get("/product-usage/options").then(({ data: d }) => setOptions(d)).catch(() => {});
    api.get("/product-usage", { params: { from_date: today, to_date: today } }).then(({ data: d }) => setTodayUse(d.rows)).catch(() => {});
  }, [today]);
  useEffect(() => { loadUsage(); }, [loadUsage]);

  const logUse = async (e) => {
    e.preventDefault();
    if (!use.product_id || !Number(use.qty)) { toast.error("Pick a product and quantity"); return; }
    try {
      const { data: d } = await api.post("/product-usage", { product_id: use.product_id, qty: Number(use.qty), note: use.note, staff_id: staffId });
      toast.success(`Logged — ${d.stock_left} ${d.unit} left in stock`);
      setUse({ product_id: "", qty: "", note: "" });
      loadUsage();
    } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
  };

  useEffect(() => {
    api.get("/staff").then(({ data }) => setStaff(data)).catch(() => {});
  }, []);

  const load = useCallback((id) => {
    if (!id) return;
    api.get("/staff-view/today", { params: { staff_id: id } }).then(({ data }) => setData(data)).catch(() => {});
  }, []);

  useEffect(() => { load(staffId); }, [staffId, load]);

  const pick = (e) => {
    setStaffId(e.target.value);
    localStorage.setItem("staff_day_id", e.target.value);
  };

  return (
    <div className="space-y-6" data-testid="staff-day-page">
      <PageHeader title="My Day" subtitle="Your appointments and commissions for today." />
      <select data-testid="staff-day-select" className={`${inputCls} max-w-xs`} value={staffId} onChange={pick}>
        <option value="">Who are you?</option>
        {staff.map((m) => <option key={m.id} value={m.id}>{`${m.name} — ${m.role}`}</option>)}
      </select>

      {data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white border border-[#E5E7E5] rounded-md p-5 shadow-sm">
              <p className="text-xs uppercase tracking-wider text-[#5C6A63] font-semibold flex items-center gap-2"><CalendarDays className="w-4 h-4 text-[#C5A059]" /> Today's Appointments</p>
              <p className="text-3xl font-bold text-[#0A3F2F] mt-2" data-testid="staff-day-appt-count">{data.appointments.length}</p>
            </div>
            <div className="bg-white border border-[#E5E7E5] rounded-md p-5 shadow-sm">
              <p className="text-xs uppercase tracking-wider text-[#5C6A63] font-semibold flex items-center gap-2"><IndianRupee className="w-4 h-4 text-[#C5A059]" /> My Service Value Today</p>
              <p className="text-3xl font-bold text-[#0A3F2F] mt-2">{fmt(data.allocated_total)}</p>
            </div>
            <div className="bg-[#0A3F2F] rounded-md p-5 shadow-sm">
              <p className="text-xs uppercase tracking-wider text-white/60 font-semibold">My Commission Today</p>
              <p className="text-3xl font-bold text-[#C5A059] mt-2" data-testid="staff-day-commission">{fmt(data.commission_total)}</p>
            </div>
          </div>

          <div className="bg-white border border-[#E5E7E5] rounded-md p-5 shadow-sm">
            <h2 className="text-sm font-bold text-[#1C1C1C] mb-4">Appointments — {data.date}</h2>
            <div className="space-y-2" data-testid="staff-day-appointments">
              {data.appointments.length === 0 && <p className="text-sm text-[#5C6A63]">No appointments assigned today.</p>}
              {data.appointments.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-3 bg-[#FAFAF8] rounded-md px-4 py-3 text-sm">
                  <div>
                    <p className="font-semibold text-[#1C1C1C]">{a.time} — {a.service_name}</p>
                    <p className="text-xs text-[#5C6A63]">{a.customer_name} · {a.phone}{a.notes ? ` · Note: ${a.notes}` : ""}</p>
                  </div>
                  <StatusBadge status={a.status} />
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-[#E5E7E5] rounded-md p-5 shadow-sm">
            <h2 className="text-sm font-bold text-[#1C1C1C] mb-4">My Commission Entries Today</h2>
            <div className="space-y-2" data-testid="staff-day-commissions">
              {data.commissions.length === 0 && <p className="text-sm text-[#5C6A63]">No commissions logged yet today.</p>}
              {data.commissions.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-3 bg-[#FAFAF8] rounded-md px-4 py-2.5 text-sm">
                  <div>
                    <p className="font-semibold text-[#1C1C1C]">{c.item_name} <span className="text-xs text-[#5C6A63]">({c.percent}% share)</span></p>
                    <p className="text-xs text-[#5C6A63]">{c.customer_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-[#0A3F2F]">{fmt(c.commission_amount)}</p>
                    <p className="text-[10px] text-[#5C6A63]">of {fmt(c.allocated_value)} @ {c.commission_rate}%</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white border border-[#E5E7E5] rounded-md p-5 shadow-sm">
            <h2 className="flex items-center gap-2 text-sm font-bold text-[#1C1C1C] mb-1"><FlaskConical className="w-4 h-4 text-[#C5A059]" /> Log Product Use</h2>
            <p className="text-xs text-[#5C6A63] mb-4">Used a colour tube, shampoo or any product for a client? Log it here — stock updates automatically.</p>
            <form onSubmit={logUse} className="grid sm:grid-cols-4 gap-2" data-testid="staff-usage-form">
              <select data-testid="staff-usage-product-select" className={inputCls} value={use.product_id} onChange={(e) => setUse({ ...use, product_id: e.target.value })}>
                <option value="">Select product…</option>
                {options.products.map((p) => <option key={p.id} value={p.id}>{`${p.name} (${p.stock} ${p.unit} left)`}</option>)}
              </select>
              <input data-testid="staff-usage-qty-input" type="number" step="any" min="0" className={inputCls} placeholder="Qty" value={use.qty} onChange={(e) => setUse({ ...use, qty: e.target.value })} />
              <input data-testid="staff-usage-note-input" className={inputCls} placeholder="Note (optional)" value={use.note} onChange={(e) => setUse({ ...use, note: e.target.value })} />
              <button type="submit" data-testid="staff-usage-submit-btn" className="bg-[#0A3F2F] text-white font-semibold rounded-full text-sm px-4 py-2.5 hover:bg-[#0d5540]">Log Use</button>
            </form>
            {todayUse.length > 0 && (
              <div className="space-y-2 mt-4" data-testid="staff-usage-list">
                <p className="text-[11px] uppercase tracking-wider text-[#5C6A63] font-semibold">Logged today</p>
                {todayUse.map((u) => (
                  <div key={u.id} className="flex items-center justify-between gap-3 bg-[#FAFAF8] rounded-md px-4 py-2.5 text-sm" data-testid={`staff-usage-row-${u.id}`}>
                    <span className="font-semibold text-[#1C1C1C]">{u.product_name} × {u.qty} {u.unit}</span>
                    <span className="text-xs text-[#5C6A63]">{u.staff_name || "—"}{u.note ? ` · ${u.note}` : ""}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
